import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  listAccountingSubscriptions,
  markSubscriptionsAsInvoiced,
  listSubscriptionPaymentRecords,
} from "../../api/accountingApi";
import "./AdminAccountingFatturazione.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const CONTAB_LABELS = {
  non_fatturabile: "Non fatturabile",
  da_fatturare: "Da fatturare",
  fatturato: "Fatturato",
};

const EMPTY_TOTALS = {
  subscription_cents: 0,
  ns_subscription_cents: 0,
  flat_fee_cents: 0,
  ns_total_cents: 0,
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function centsToEur(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "0,00";
  return (n / 100).toFixed(2).replace(".", ",");
}

function todayYMD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function ymdToDisplay(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function displayToYmd(display) {
  if (!display) return "";
  const clean = String(display).trim();
  const m = clean.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const valid = d.getFullYear() === Number(yyyy) && d.getMonth() + 1 === Number(mm) && d.getDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : "";
}

function normalizeDateTyping(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function DateFilterInput({ label, value, onChange }) {
  const [draft, setDraft] = useState(ymdToDisplay(value));
  const nativeRef = useRef(null);

  useEffect(() => setDraft(ymdToDisplay(value)), [value]);

  const openPicker = () => {
    if (nativeRef.current?.showPicker) nativeRef.current.showPicker();
    else nativeRef.current?.click();
  };

  const handleTextChange = (e) => {
    const nextDraft = normalizeDateTyping(e.target.value);
    setDraft(nextDraft);
    onChange(displayToYmd(nextDraft));
  };

  const handleBlur = () => {
    const ymd = displayToYmd(draft);
    if (ymd) {
      setDraft(ymdToDisplay(ymd));
      onChange(ymd);
    } else if (!draft.trim()) {
      onChange("");
      setDraft("");
    } else {
      setDraft(value ? ymdToDisplay(value) : "");
    }
  };

  return (
    <div className="acc2-filter-group">
      <label className="acc2-label">{label}</label>
      <div className="acc2-date-wrap">
        <input
          type="text"
          inputMode="numeric"
          className="acc2-input acc2-input--date-text"
          value={draft}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder="gg/mm/aaaa"
        />
        <button type="button" className="acc2-date-btn" onClick={openPicker} title={`Apri calendario ${label}`}>
          📅
        </button>
        <input
          ref={nativeRef}
          type="date"
          className="acc2-native-date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

export default function AdminAccountingAbbonamenti() {
  const [filters, setFilters] = useState({
    subscription_from: "",
    subscription_to: "",
    invoice_from: "",
    invoice_to: "",
    fornitore_id: "",
    contab_status: "all",
  });

  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [paymentRecords, setPaymentRecords] = useState({ data: [], unresolved_events: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [invoiceMeta, setInvoiceMeta] = useState({ number: "", date: todayYMD() });
  const [sort, setSort] = useState({ field: "created_date", dir: "desc" });

  const token = localStorage.getItem("token");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/accounting/fornitori-options`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });
        const js = await res.json();
        if (!res.ok) throw new Error(js?.error || `HTTP ${res.status}`);
        if (alive) setSuppliers(Array.isArray(js) ? js : []);
      } catch (err) {
        if (alive) toast.error(err?.message || "Errore caricamento fornitori");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  async function loadData(nextPage = 1, overrideFilters = filters) {
    try {
      setLoading(true);
      setSelectedIds([]);
      const res = await listAccountingSubscriptions({ page: nextPage, pageSize, filters: overrideFilters });
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotals(res.totals || EMPTY_TOTALS);
      setPage(res.page || nextPage);
      setTotalPages(res.totalPages || 1);
      setPaymentRecords(await listSubscriptionPaymentRecords(overrideFilters));
    } catch (err) {
      console.error("[AbbonamentiAccounting] loadData error:", err);
      toast.error(err?.message || "Errore caricamento abbonamenti contabili");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(1, filters); /* eslint-disable-next-line */ }, []);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { field, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = field === "subscription_cents" || field === "ns_total_cents"
        ? Number(a[field]) || 0
        : String(a[field] || "");
      const vb = field === "subscription_cents" || field === "ns_total_cents"
        ? Number(b[field]) || 0
        : String(b[field] || "");
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
      return va.localeCompare(vb, "it", { sensitivity: "base" }) * factor;
    });
    return arr;
  }, [rows, sort]);

  const selectableIds = sortedRows.filter((r) => r.contab_status === "da_fatturare").map((r) => r.id);
  const allSelectableChecked = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  function handleSort(field) {
    setSort((prev) => (prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" }));
  }

  function renderSortIcon(field) {
    if (sort.field !== field) return <span className="acc2-sort-icon">↕</span>;
    return <span className="acc2-sort-icon acc2-sort-icon--active">{sort.dir === "asc" ? "↑" : "↓"}</span>;
  }

  function toggleOne(id) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function toggleAll() {
    setSelectedIds(allSelectableChecked ? [] : selectableIds);
  }

  async function handleMarkAsInvoiced() {
    if (!selectedIds.length) {
      toast.error("Seleziona almeno un abbonamento da fatturare");
      return;
    }
    try {
      setSavingInvoice(true);
      const res = await markSubscriptionsAsInvoiced(selectedIds, { invoice: invoiceMeta });
      toast.success(`Fattura ${String(res.invoice_number).padStart(3, "0")}/${res.invoice_year} creata`);
      setInvoiceMeta({ number: "", date: todayYMD() });
      await loadData(page, filters);
    } catch (err) {
      console.error("[AbbonamentiAccounting] mark invoiced error:", err);
      toast.error(err?.message || "Errore fatturazione abbonamenti");
    } finally {
      setSavingInvoice(false);
    }
  }

  const handleFilterChange = (field) => (e) => {
    const next = { ...filters, [field]: e.target.value };
    setFilters(next);
    loadData(1, next);
  };

  const handleDateFilterChange = (field) => (value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);
  };

  const applyDateFilters = () => loadData(1, filters);
  const resetFilters = () => {
    const cleared = { subscription_from: "", subscription_to: "", invoice_from: "", invoice_to: "", fornitore_id: "", contab_status: "all" };
    setFilters(cleared);
    loadData(1, cleared);
  };

  return (
    <div className="acc2-page">
      <div className="acc2-inner">
        <header className="acc2-header">
          <div>
            <h1>Fatturazione abbonamenti</h1>
            <p className="acc2-header-sub">Primi checkout e rinnovi: ogni canone pagato applica le condizioni degli ordini. Le quote dei pending sono teoriche; rimborsi e periodi non risolti richiedono verifica.</p>
          </div>
        </header>

        <details className="acc2-filters">
          <summary>Registro Stripe: ultimi 100 canoni osservati — eventi da riconciliare: {paymentRecords.unresolved_events}</summary>
          <p>Il primo canone è già rappresentato dal checkout. I rinnovi pagati e collegati sono nell’elenco contabile. Rimborsi, collegamenti incerti e periodi non risolti richiedono verifica; non rettificano automaticamente i documenti emessi. Questo registro segue fornitore e date pagamento, senza i filtri delle proforme.</p>
          <div style={{ overflowX: "auto" }}><table className="acc2-table"><thead><tr><th>Fattura Stripe</th><th>Periodo</th><th>Stato</th><th>Pagato</th><th>Rimborsato</th><th>Incasso netto</th><th>Collegamento</th></tr></thead><tbody>
            {paymentRecords.data.map(r => <tr key={r.stripe_invoice_id}>
              <td>{r.stripe_invoice_id}</td><td>{formatDate(r.period_start)} — {formatDate(r.period_end)}</td><td>{r.payment_state}</td>
              <td>{centsToEur(r.amount_paid_cents)} {r.currency}</td><td>{centsToEur(r.amount_refunded_cents)} {r.currency}</td><td>{centsToEur(Number(r.amount_paid_cents) - Number(r.amount_refunded_cents))} {r.currency}</td>
              <td>{r.accounting_coverage === "initial_checkout" ? "Primo checkout" : r.accounting_coverage === "renewal" ? "Rinnovo" : "Da verificare"}</td>
            </tr>)}
          </tbody></table></div>
        </details>

        <section className="acc2-filters">
          <div className="acc2-filters-row">
            <DateFilterInput label="Da pagamento" value={filters.subscription_from} onChange={handleDateFilterChange("subscription_from")} />
            <DateFilterInput label="A pagamento" value={filters.subscription_to} onChange={handleDateFilterChange("subscription_to")} />
            <DateFilterInput label="Da fattura" value={filters.invoice_from} onChange={handleDateFilterChange("invoice_from")} />
            <DateFilterInput label="A fattura" value={filters.invoice_to} onChange={handleDateFilterChange("invoice_to")} />

            <div className="acc2-filter-group">
              <label className="acc2-label">Fornitore</label>
              <select className="acc2-input" value={filters.fornitore_id} onChange={handleFilterChange("fornitore_id")}>
                <option value="">Tutti</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.label || s.nome || `Fornitore #${s.id}`}</option>
                ))}
              </select>
            </div>

            <div className="acc2-filter-group">
              <label className="acc2-label">Stato</label>
              <select className="acc2-input" value={filters.contab_status} onChange={handleFilterChange("contab_status")}>
                <option value="all">Tutti</option>
                <option value="da_fatturare">Da fatturare</option>
                <option value="fatturato">Fatturato</option>
                <option value="non_fatturabile">Non fatturabile</option>
              </select>
            </div>

            <button type="button" className="acc2-btn acc2-btn--primary" onClick={applyDateFilters}>Applica date</button>
            <button type="button" className="acc2-btn acc2-btn--ghost" onClick={resetFilters}>Reset</button>
          </div>
        </section>

        <section className="acc2-table-section">
          <div className="acc2-table-wrapper">
            <table className="acc2-table">
              <thead>
                <tr>
                  <th className="acc2-th acc2-th--check">
                    <input type="checkbox" checked={allSelectableChecked} onChange={toggleAll} disabled={!selectableIds.length} />
                  </th>
                  <th className="acc2-th acc2-th--date acc2-th--sortable" onClick={() => handleSort("created_date")}>Data checkout / pagamento {renderSortIcon("created_date")}</th>
                  <th className="acc2-th acc2-th--id acc2-th--sortable" onClick={() => handleSort("id")}>ID {renderSortIcon("id")}</th>
                  <th className="acc2-th acc2-th--cliente acc2-th--sortable" onClick={() => handleSort("cliente")}>Cliente {renderSortIcon("cliente")}</th>
                  <th className="acc2-th acc2-th--fornitore acc2-th--sortable" onClick={() => handleSort("fornitore")}>Fornitore {renderSortIcon("fornitore")}</th>
                  <th className="acc2-th acc2-th--city">Città</th>
                  <th className="acc2-th acc2-th--status">Stato</th>
                  <th className="acc2-th acc2-th--cliente">Piano</th>
                  <th className="acc2-th acc2-th--date">Ritiro</th>
                  <th className="acc2-th acc2-th--num acc2-th--sortable" onClick={() => handleSort("subscription_cents")}>Importo {renderSortIcon("subscription_cents")}</th>
                  <th className="acc2-th acc2-th--num">Ns %</th>
                  <th className="acc2-th acc2-th--num">Flat</th>
                  <th className="acc2-th acc2-th--num acc2-th--sortable" onClick={() => handleSort("ns_total_cents")}>Totale ns {renderSortIcon("ns_total_cents")}</th>
                  <th className="acc2-th acc2-th--status">Fattura</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr className="acc2-row acc2-row--loading"><td colSpan={14}>Caricamento abbonamenti…</td></tr>}
                {!loading && sortedRows.length === 0 && <tr className="acc2-row acc2-row--empty"><td colSpan={14}>Nessun abbonamento trovato.</td></tr>}
                {!loading && sortedRows.map((r) => {
                  const selectable = r.contab_status === "da_fatturare";
                  return (
                    <tr key={r.id} className="acc2-row">
                      <td className="acc2-cell acc2-cell--check">
                        <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleOne(r.id)} disabled={!selectable} />
                      </td>
                      <td className="acc2-cell acc2-cell--date">{formatDate(r.created_date)}</td>
                      <td className="acc2-cell acc2-cell--id">{r.source_type === "renewal" ? `R${r.payment_record_id}` : r.id}</td>
                      <td className="acc2-cell acc2-cell--cliente" title={r.cliente}>{r.cliente}</td>
                      <td className="acc2-cell acc2-cell--fornitore" title={r.fornitore}>{r.fornitore}</td>
                      <td className="acc2-cell acc2-cell--city">{r.citta || "—"}</td>
                      <td className="acc2-cell acc2-cell--status"><span className={`acc2-pill acc2-pill--${r.contab_status}`}>{CONTAB_LABELS[r.contab_status] || r.contab_status}</span><small> {r.raw_status === "paid" ? "Pagato" : r.raw_status === "pending" ? "In attesa — quote teoriche" : r.raw_status}{r.subscription_link_status === "ambiguous" ? " · Collegamento ambiguo" : ""}{r.source_type === "renewal" ? ` · Rinnovo ${r.stripe_invoice_id}` : " · Primo checkout"}</small></td>
                      <td className="acc2-cell acc2-cell--cliente" title={r.plan_name}>{r.plan_name}</td>
                      <td className="acc2-cell acc2-cell--date">{r.pickup_day_name ? `${r.pickup_day_name} ${String(r.pickup_start || "").slice(0,5)}-${String(r.pickup_end || "").slice(0,5)}` : "—"}</td>
                      <td className="acc2-cell acc2-cell--num">{centsToEur(r.subscription_cents)}</td>
                      <td className="acc2-cell acc2-cell--num">{centsToEur(r.ns_subscription_cents)}</td>
                      <td className="acc2-cell acc2-cell--num">{centsToEur(r.flat_fee_cents)}</td>
                      <td className="acc2-cell acc2-cell--num acc2-cell--total">{centsToEur(r.ns_total_cents)}</td>
                      <td className="acc2-cell acc2-cell--status">{r.invoice_number || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="acc2-bottom-actions">
            <div className="acc2-totals-panel">
              <div className="acc2-totals-line">
                <span className="acc2-totals-line-label">Filtro corrente — {totals.checkout_count || 0} checkout:</span>
                <span className="acc2-totals-chip">Pagati ({totals.paid_count || 0}): {centsToEur(totals.paid_cents)} €</span>
                <span className="acc2-totals-chip">Rimborsati: {centsToEur(totals.refunded_cents)} € · Incasso netto: {centsToEur(Number(totals.paid_cents || 0) - Number(totals.refunded_cents || 0))} €</span>
                <span className="acc2-totals-chip">Pending ({totals.pending_count || 0}): {centsToEur(totals.pending_cents)} €</span>
                <span className="acc2-totals-chip">Quote maturate: {centsToEur(totals.earned_commission_cents)} €</span>
                <span className="acc2-totals-chip">Quote teoriche pending: {centsToEur(totals.pending_theoretical_commission_cents)} €</span>
                <span className="acc2-totals-chip"><span className="acc2-totals-chip-label">Abbonamenti</span><span className="acc2-totals-chip-value">{centsToEur(totals.subscription_cents)} €</span></span>
                <span className="acc2-totals-chip"><span className="acc2-totals-chip-label">Ns %</span><span className="acc2-totals-chip-value">{centsToEur(totals.ns_subscription_cents)} €</span></span>
                <span className="acc2-totals-chip"><span className="acc2-totals-chip-label">Flat</span><span className="acc2-totals-chip-value">{centsToEur(totals.flat_fee_cents)} €</span></span>
                <span className="acc2-totals-chip acc2-totals-chip--strong"><span className="acc2-totals-chip-label">Quote complessive incluse teoriche</span><span className="acc2-totals-chip-value acc2-totals-chip-value--strong">{centsToEur(totals.ns_total_cents)} €</span></span>
              </div>
            </div>

            <div className="acc2-invoice-controls">
              <div className="acc2-invoice-field">
                <label className="acc2-label">N. fattura</label>
                <input className="acc2-input acc2-input--sm" value={invoiceMeta.number} onChange={(e) => setInvoiceMeta((m) => ({ ...m, number: e.target.value }))} placeholder="auto" />
              </div>
              <div className="acc2-invoice-field">
                <label className="acc2-label">Data fattura</label>
                <input className="acc2-input acc2-input--sm" type="date" value={invoiceMeta.date} onChange={(e) => setInvoiceMeta((m) => ({ ...m, date: e.target.value }))} />
              </div>
              <button type="button" className="acc2-btn acc2-btn--primary" onClick={handleMarkAsInvoiced} disabled={savingInvoice || !selectedIds.length}>
                {savingInvoice ? "Salvo…" : `Fattura selezionati (${selectedIds.length})`}
              </button>
            </div>

            <div className="acc2-pagination">
              <button type="button" className="acc2-btn acc2-btn--ghost acc2-btn--sm" disabled={page <= 1 || loading} onClick={() => loadData(page - 1, filters)}>← Prec.</button>
              <span className="acc2-page-info">Pagina {page} / {totalPages}</span>
              <button type="button" className="acc2-btn acc2-btn--ghost acc2-btn--sm" disabled={page >= totalPages || loading} onClick={() => loadData(page + 1, filters)}>Succ. →</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
